import { Button, Card } from 'components/atoms';
import { FormInput, LabeledStatus, ViewToggleButton } from 'components/molecules';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useIsUserBanned } from 'hooks/useIsUserBanned';
import { useListViewById } from 'hooks/useListViewById';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiList, PiPlus, PiSquaresFour } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { STATUS } from 'services/types';
import { useGetVideosListInfinite } from 'services/video';
import { IVideoSearch, IVideosResponse } from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

const videoSearchInitialValues: IVideoSearch = {
  title: '',
  isrc: '',
  ean: '',
  artistNickname: '',
  artistName: '',
};

const Videos = () => {
  const [filters, setFilters] = useState<IVideoSearch>({});

  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useGetVideosListInfinite(filters);
  const scroll = useScroll({ fetchNextPage: fetchNextPage });
  const tableData = getNodes<IVideosResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'video';
  const viewId = 'video';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const { getSelectOptions } = useEnumToSelectOptions();

  const { isBanned } = useIsUserBanned();

  const { view } = useListViewById(viewId, 'grid');

  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>
              {!isBanned && (
                <Button
                  onlyIcon
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/data')}
                >
                  <Button.Icon icon={<PiPlus />} />
                  <Button.Description description={t('button.add')} />
                </Button>
              )}
            </>
          }
        >
          <QueryFilter<IVideoSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={videoSearchInitialValues}
          >
            <>
              <QueryFilter.Form<IVideoSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setFilters(values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text appBackground name="title" label={t('input.label.title')} />
                  <FormInput.Text appBackground name="isrc" label={t('input.label.isrc')} />
                  <FormInput.Text appBackground name="ean" label={t('input.label.ean')} />
                  <FormInput.Text
                    appBackground
                    name="artistNickname"
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    appBackground
                    name="artistName"
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasDuplicatedArtist"
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasContracts"
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="video" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={tableData.nodes}
        item={(video, key) => (
          <Card
            view={view}
            isVideo={true}
            key={video.id}
            onClick={() => navigate(`/video/${video.id}/data`)}
            header={video.mainTitle.title}
            description={video.mainTitle.version}
            status={<LabeledStatus status={video.videoStatus} noname />}
          />
        )}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
      />
    </TopbarPage>
  );
};

export default Videos;
